// ─── Org chart tree maths ─────────────────────────────────────────────────────
//
// Pulled out of OrgChartPage.jsx so it can be tested without a browser. The
// component owns the markup; the arithmetic that decides who appears, how wide
// each card's slot is, and where the view starts lives here.
//
// See tests/org-chart-tree.test.mjs for the faults each function exists to fix.

/** Zoom limits, shared by the wheel handler, the +/- buttons and fit-to-view. */
export const MIN_SCALE = 0.2;
export const MAX_SCALE = 3;

/**
 * The ids of everyone sitting on a reporting loop: A manages B who manages A,
 * or somebody recorded as their own manager.
 *
 * Real data does produce these. `reporting_manager_id` is a free-form field on
 * the People form and nothing on the way in checks that the chain terminates,
 * so one mis-set manager is enough.
 */
export function reportingCycles(people) {
  const managerOf = new Map((people || []).map(p => [p.id, p.reporting_manager_id]));
  const onCycle = new Set();
  const settled = new Set(); // chains already walked, so each is followed once

  for (const p of people || []) {
    if (settled.has(p.id)) continue;
    const path = [];
    const seenAt = new Map();
    let id = p.id;
    // Climb until the chain runs out, reaches somebody already known to
    // terminate, or comes back to itself.
    while (id != null && managerOf.has(id) && !settled.has(id) && !seenAt.has(id)) {
      seenAt.set(id, path.length);
      path.push(id);
      id = managerOf.get(id);
    }
    if (id != null && seenAt.has(id)) {
      // Everything from the first sighting onward is the loop. Anyone standing
      // on the path leading INTO it is not on it, and is left alone.
      for (let i = seenAt.get(id); i < path.length; i++) onCycle.add(path[i]);
    }
    path.forEach(x => settled.add(x));
  }
  return onCycle;
}

/**
 * The reporting hierarchy as a forest of `{ ...person, children: [] }` nodes.
 *
 * Every person handed in comes back exactly once, whatever the data does. That
 * is the point: the previous version made a node a root only when its manager
 * was missing from the list, and somebody in a reporting loop always has a
 * manager who is present, so they were attached to a branch that hung off
 * nothing and disappeared from the chart along with everyone below them.
 */
export function buildTree(people) {
  const nodes = new Map();
  (people || []).forEach(p => nodes.set(p.id, { ...p, children: [] }));

  const cycled = reportingCycles(people);

  const roots = [];
  (people || []).forEach(p => {
    const node = nodes.get(p.id);
    const managerId = p.reporting_manager_id;
    const manager = managerId && managerId !== p.id ? nodes.get(managerId) : null;
    // A manager who is not in this list has been filtered out (archived, or
    // left), so their reports stand as roots of their own.
    if (manager && !cycled.has(p.id)) manager.children.push(node);
    else roots.push(node);
  });
  return roots;
}

/** Every level ordered by surname then forename, in place. */
export function sortTree(nodes) {
  nodes.sort((a, b) => `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`));
  nodes.forEach(n => sortTree(n.children));
  return nodes;
}

/**
 * How many card-widths of horizontal room a node needs, counting only what is
 * actually on screen.
 *
 * The old `leafCount` counted every descendant whether it was rendered or not,
 * so a collapsed manager hiding twelve people still reserved twelve slots. With
 * the chart's default collapse that left roughly three quarters of the canvas
 * empty and pushed cards thousands of pixels away from their own parent.
 */
export function visibleLeaves(node, isCollapsed = () => false) {
  if (!node.children || node.children.length === 0) return 1;
  if (isCollapsed(node.id)) return 1;
  return node.children.reduce((sum, c) => sum + visibleLeaves(c, isCollapsed), 0);
}

/**
 * A transform that shows the whole chart, centred.
 *
 * Never magnifies: a four-person org stays at its natural size rather than being
 * blown up to fill the pane. A chart too big to fit even at MIN_SCALE is shown at
 * MIN_SCALE and panned, rather than shrunk to something unreadable.
 */
export function fitTransform({ contentW, contentH, viewW, viewH, padding = 0 }) {
  const availW = viewW - padding * 2;
  const availH = viewH - padding * 2;
  // Called once before the pane has been laid out, where every dimension is 0.
  // Leave the view alone rather than dividing by zero.
  if (!(contentW > 0) || !(contentH > 0) || !(availW > 0) || !(availH > 0)) {
    return { x: 0, y: 0, scale: 1 };
  }
  const scale = Math.min(
    MAX_SCALE,
    Math.max(MIN_SCALE, Math.min(1, availW / contentW, availH / contentH)),
  );
  return {
    x: (viewW - contentW * scale) / 2,
    // Hung from the top, not centred vertically. Centring a short hierarchy in a
    // tall pane leaves a band of empty space above the chief executive and
    // another below the most junior person, which on screen reads as a chart
    // that has failed to draw. An org chart is read downward from its root.
    y: padding,
    scale,
  };
}

/**
 * Zoom by `factor` about the pane-relative point (px, py), so whatever is under
 * the pointer stays under it. Scaling about the canvas origin instead, as this
 * did, slides the chart away from the thing you were trying to look at.
 */
export function zoomAt(transform, factor, px, py) {
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, transform.scale * factor));
  const contentX = (px - transform.x) / transform.scale;
  const contentY = (py - transform.y) / transform.scale;
  return { x: px - contentX * scale, y: py - contentY * scale, scale };
}

/** The chain of managers above a person, nearest first. Used to reveal a search hit. */
export function ancestorIds(people, personId) {
  const managerOf = new Map((people || []).map(p => [p.id, p.reporting_manager_id]));
  const out = [];
  const seen = new Set([personId]);
  let id = managerOf.get(personId);
  while (id != null && managerOf.has(id) && !seen.has(id)) {
    out.push(id);
    seen.add(id);
    id = managerOf.get(id);
  }
  return out;
}

const bySurname = (a, b) => `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`);

/**
 * The team board: one column per team that has anyone in it, by team name, then
 * an Unassigned column for people with no team. Membership, not reporting, so
 * contractors are included. The team's lead, when they are in the list, heads
 * the column rather than appearing among its members.
 *
 * `teams` is GET /teams, which is active teams only; a person on an inactive
 * team still gets a column, named from their own `team_name`.
 */
export function groupByTeam(people, teams) {
  const teamById = new Map((teams || []).map(t => [t.id, t]));
  const cols = new Map();
  for (const p of people || []) {
    const key = p.team_id || null;
    if (!cols.has(key)) {
      const t = teamById.get(key);
      cols.set(key, {
        key,
        name: key ? (t?.name || p.team_name || 'Unknown team') : 'Unassigned',
        color: t?.color || null,
        leadId: t?.lead_id || null,
        lead: null,
        members: [],
      });
    }
    const col = cols.get(key);
    if (col.leadId && p.id === col.leadId) col.lead = p;
    else col.members.push(p);
  }
  const board = [...cols.values()].map(({ leadId, ...c }) => ({ ...c, members: c.members.sort(bySurname) }));
  board.sort((a, b) => (a.key === null) - (b.key === null) || a.name.localeCompare(b.name));
  return board;
}
